/**
 * Add/Edit Location dialog (DNKADM-0051 U1) — a MUI `Dialog` that creates/updates an
 * owned `v2_location` via `createLocation` / `updateLocation` (handoff §5.2).
 *
 * Fields: **name** (required), **timezone** (required IANA, chosen from a strict
 * `Autocomplete` picker sourced from native `Intl.supportedValuesOf('timeZone')`,
 * DNKADM-0077 — the location owns the governing zone, §4.5; NO `@mui/x-date-pickers`,
 * no tz library), a single free-text **address**, a single free-text **map link**
 * (`mapUrl`, DNKAPI-0164), optional **phone** (DNKAPI-0143 — the structured
 * `addressLine1`/`city`/`state`/`postalCode` fields are removed), the two independent
 * **resource nouns** (singular/plural — plural entered explicitly, never derived) and
 * the **Default location** checkbox (DNKADM-0267 — always sent on save; the backend
 * field is a primitive boolean). `isActive` is NOT set here — activation is the
 * list's deactivate/reactivate affordance (§4.3).
 *
 * Client validation (mirrors the API) blocks Save only while name/timezone are empty.
 * `mapUrl` is a freeform optional URL with **no validation** (DNKAPI-0164 doesn't
 * validate it either) — the old geo range/both-or-neither rules are gone. On a
 * successful create/update the parent **refetches** (the caller's `onSaved`); a
 * validation `400` / `409` renders INLINE, anything else raises an error toast
 * (`classifyFormError`).
 *
 * **In a drawer (DNKADM-0495).** With `drawer`, an edit opens in the right-side
 * {@link EditDrawer} (one Save, one Cancel) instead of a dialog — the location page's
 * Basics drawer. Same fields, same validation, same PUT body. A failure of either kind
 * shows inline and the drawer stays open on it; success closes it.
 */
import { useId, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
} from '@mui/material';
import {
  createLocation,
  updateLocation,
  type LocationInput,
  type V2Location,
} from '@/api/locations';
import type { ShowToast } from '@/components/toastContext';
import { PlanLimitDialog } from '@/components/PlanLimitDialog';
import { parsePlanLimit, v2ErrorCode, type PlanLimitInfo } from '@/api/v2Errors';
import { classifyFormError } from '@/api/screenError';
import { EditDrawer } from '@/features/services/EditDrawer';
import { listTimeZones, resolvedTimeZone } from './timezones';
import { PhoneField } from '@/components/phone/PhoneField';

export interface LocationDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The location being edited, or `null` to add a new one. */
  location: V2Location | null;
  /** Close the dialog without saving. */
  onClose: () => void;
  /** Called after a successful create/update so the caller can refetch the list. */
  onSaved: () => void;
  /** Raise a non-blocking toast (upstream-error). */
  showToast: ShowToast;
  /** Open an edit in the right-side drawer instead of a dialog (DNKADM-0495). */
  drawer?: boolean;
}

interface FieldErrors {
  name?: string;
  timezone?: string;
  billingAnchorDay?: string;
}

export function LocationDialog(props: LocationDialogProps) {
  // Remount the inner form per open / per edited location so its inputs reseed.
  if (!props.open) return null;
  return <LocationDialogForm key={props.location?.id ?? 'new'} {...props} />;
}

/** Trim a string and return `undefined` when empty (omit the optional key). */
function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function LocationDialogForm({
  location,
  onClose,
  onSaved,
  showToast,
  drawer = false,
}: Omit<LocationDialogProps, 'open'>) {
  const ids = useId();
  const isEdit = location !== null;
  const inDrawer = drawer && isEdit;

  const [name, setName] = useState(location?.name ?? '');
  // A NEW location defaults to the browser's resolved zone (the common case needs no
  // interaction); EDIT seeds the stored zone verbatim — including a legacy/invalid one
  // (DNKADM-0077), which `timeZoneOptions` unions in so the strict picker can show it.
  const [timezone, setTimezone] = useState(location?.timezone ?? resolvedTimeZone());

  // The canonical IANA list, plus the current value when it isn't on it — so an
  // already-stored legacy zone (e.g. an old `"Chicago/Central"`) loads and is visibly
  // correctable rather than silently blanked. No data is rewritten here.
  const timeZoneOptions = useMemo(() => {
    const zones = listTimeZones();
    return timezone !== '' && !zones.includes(timezone) ? [timezone, ...zones] : zones;
  }, [timezone]);
  const [address, setAddress] = useState(location?.address ?? '');
  const [mapUrl, setMapUrl] = useState(location?.mapUrl ?? '');
  const [phone, setPhone] = useState(location?.phone ?? '');
  // Resource nouns + default flag (DNKADM-0267). `isDefault` preloads the stored
  // value and is ALWAYS sent on save — the backend field is a primitive boolean, so
  // omitting it would silently unset the tenant's default location.
  const [resourceNounSingular, setResourceNounSingular] = useState(
    location?.resourceNounSingular ?? '',
  );
  const [resourceNounPlural, setResourceNounPlural] = useState(
    location?.resourceNounPlural ?? '',
  );
  const [isDefault, setIsDefault] = useState(location?.isDefault ?? false);
  // Billing anchor day override (DNKADM-0374). Blank when not set; '1'–'28' when set.
  const [billingAnchorDay, setBillingAnchorDay] = useState(
    location?.billingAnchorDay !== undefined ? String(location.billingAnchorDay) : '',
  );

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // The plan-limit popup payload (DNKADM-0065) when a create is capped; null = closed.
  const [planLimit, setPlanLimit] = useState<PlanLimitInfo | null>(null);

  function validate(): LocationInput | null {
    const errors: FieldErrors = {};
    const trimmedName = name.trim();
    const trimmedTz = timezone.trim();
    if (trimmedName === '') errors.name = 'Enter a location name.';
    if (trimmedTz === '') errors.timezone = 'Enter an IANA timezone (e.g. America/New_York).';

    // Billing anchor day (DNKADM-0374): blank OK; else whole number 1–28.
    const billingDayTrimmed = billingAnchorDay.trim();
    let billingDayValue: number | null = null; // null = blank
    if (billingDayTrimmed !== '') {
      const n = Number(billingDayTrimmed);
      if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1 || n > 28) {
        errors.billingAnchorDay = 'Enter a whole number from 1 to 28, or leave blank.';
      } else {
        billingDayValue = n;
      }
    }

    if (errors.name || errors.timezone || errors.billingAnchorDay) {
      setFieldErrors(errors);
      return null;
    }
    setFieldErrors({});

    // Build the input, omitting absent optional keys (exactOptionalPropertyTypes).
    // `mapUrl` is a freeform optional URL — no validation (DNKAPI-0164), omit when blank.
    // `isDefault` is REQUIRED and always carried (backend primitive, DNKADM-0267).
    const input: LocationInput = { name: trimmedName, timezone: trimmedTz, isDefault };
    const addr = optional(address);
    if (addr !== undefined) input.address = addr;
    const link = optional(mapUrl);
    if (link !== undefined) input.mapUrl = link;
    const ph = optional(phone);
    if (ph !== undefined) input.phone = ph;
    const nounSingular = optional(resourceNounSingular);
    if (nounSingular !== undefined) input.resourceNounSingular = nounSingular;
    const nounPlural = optional(resourceNounPlural);
    if (nounPlural !== undefined) input.resourceNounPlural = nounPlural;
    // Billing anchor day (DNKADM-0374):
    // PUT: always send — 0 if blank (clear), day if filled. Mirror isDefault always-send rule.
    // POST: only send when filled (no override = omit key entirely).
    if (isEdit) {
      // Edit: always carry (0 = clear, day = set).
      input.billingAnchorDay = billingDayValue !== null ? billingDayValue : 0;
    } else if (billingDayValue !== null) {
      // Create: only when filled.
      input.billingAnchorDay = billingDayValue;
    }
    return input;
  }

  async function handleSave(): Promise<boolean> {
    setServerError(null);
    const input = validate();
    if (!input) return false;
    setSaving(true);
    try {
      if (isEdit && location) await updateLocation(location.id, input);
      else await createLocation(input);
      // DNKADM-0320: confirm the save (after the write resolves; one toast per action).
      showToast(isEdit ? 'Location saved' : 'Location added', 'success');
      onSaved();
      return true;
    } catch (err) {
      // Plan-limit enforcement is Add-only (DNKADM-0065 / DNKAPI-0139) — Edit is
      // never blocked, so only a create maps these codes.
      if (!isEdit) {
        const code = v2ErrorCode(err);
        if (code === 'PLAN_LIMIT_REACHED') {
          // The cap was hit — show the upgrade popup (NOT a generic toast).
          setPlanLimit(parsePlanLimit(err));
          return false;
        }
        if (code === 'PLAN_VERIFY_FAILED') {
          // Transient plan-service outage — a retry toast (NOT the popup); the
          // admin can resubmit (the create may well be under the cap).
          showToast(
            'We couldn’t verify your plan just now. Please try again in a moment.',
            'error',
          );
          return false;
        }
      }
      const view = classifyFormError(
        err,
        'The location couldn’t be saved. Please try again.',
      );
      // In the drawer the failure keeps it open, so it is said right there (DNKADM-0495).
      if (view.kind === 'inline' || inDrawer) setServerError(view.message);
      else showToast(view.message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  }

  const titleId = `${ids}-title`;

  const fields = (
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            size="small"
            required
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name}
            slotProps={{ htmlInput: { 'aria-label': 'Location name' } }}
          />
          <Autocomplete
            options={timeZoneOptions}
            value={timezone}
            onChange={(_event, value) => setTimezone(value)}
            disabled={saving}
            autoHighlight
            disableClearable
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Timezone"
                required
                error={Boolean(fieldErrors.timezone)}
                helperText={
                  fieldErrors.timezone ?? 'IANA timezone — governs this location’s hours.'
                }
                slotProps={{
                  ...params.slotProps,
                  htmlInput: { ...params.slotProps.htmlInput, 'aria-label': 'Timezone' },
                }}
              />
            )}
          />
          <TextField
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={saving}
            size="small"
            multiline
            minRows={2}
            placeholder="123 Fairway Dr, Cedar Park, TX 78613"
            helperText="The location’s full address as a single line."
            slotProps={{ htmlInput: { 'aria-label': 'Address' } }}
          />
          <TextField
            label="Map link (Google Maps URL)"
            value={mapUrl}
            onChange={(e) => setMapUrl(e.target.value)}
            disabled={saving}
            size="small"
            placeholder="https://maps.google.com/…"
            helperText="Paste a Google Maps link for this location (optional)."
            slotProps={{ htmlInput: { 'aria-label': 'Map link (Google Maps URL)' } }}
          />
          <PhoneField
            label="Phone"
            value={phone}
            onChange={setPhone}
            disabled={saving}
            aria-label="Phone"
          />
          {/* Billing anchor day override (DNKADM-0374) — after Phone, before resource nouns. */}
          <TextField
            label="Billing day of month (override)"
            value={billingAnchorDay}
            onChange={(e) => setBillingAnchorDay(e.target.value)}
            disabled={saving}
            size="small"
            error={Boolean(fieldErrors.billingAnchorDay)}
            helperText={
              fieldErrors.billingAnchorDay ??
              'Empty inherits the tenant billing day. 1–28 overrides it for memberships sold at this location. Not used when this location\'s card processor cannot anchor.'
            }
            slotProps={{ htmlInput: { inputMode: 'numeric', 'aria-label': 'Billing day of month (override)' } }}
          />
          {/* Resource nouns (DNKADM-0267) — two independent strings; the plural is
              entered explicitly, never auto-derived from the singular. */}
          <TextField
            label="Bookable resource name (singular)"
            value={resourceNounSingular}
            onChange={(e) => setResourceNounSingular(e.target.value)}
            disabled={saving}
            size="small"
            helperText="What this location calls one bookable resource, e.g. a court or a room (optional)."
            slotProps={{ htmlInput: { 'aria-label': 'Bookable resource name (singular)' } }}
          />
          <TextField
            label="Bookable resource name (plural)"
            value={resourceNounPlural}
            onChange={(e) => setResourceNounPlural(e.target.value)}
            disabled={saving}
            size="small"
            helperText="Enter the plural form explicitly — it is not derived from the singular."
            slotProps={{ htmlInput: { 'aria-label': 'Bookable resource name (plural)' } }}
          />
          <Stack spacing={0}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  disabled={saving}
                  slotProps={{ input: { 'aria-label': 'Default location' } }}
                />
              }
              label="Default location"
            />
            <FormHelperText>
              Preselected for members and staff where a location choice is offered.
            </FormHelperText>
          </Stack>

          {serverError && (
            <Alert severity="error" role="alert">
              {serverError}
            </Alert>
          )}
        </Stack>
  );

  if (inDrawer) {
    return (
      <EditDrawer open title="Edit basics" onClose={onClose} onSave={handleSave}>
        {fields}
      </EditDrawer>
    );
  }

  return (
    <>
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{isEdit ? 'Edit location' : 'Add location'}</DialogTitle>
      <DialogContent dividers>
        {fields}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save location' : 'Add location'}
        </Button>
      </DialogActions>
    </Dialog>
    <PlanLimitDialog
      open={planLimit !== null}
      info={planLimit}
      onClose={() => setPlanLimit(null)}
    />
    </>
  );
}
